import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useDocumentI18n } from './document-i18n';
import { TABLE_CONTEXT_EVENT } from './document-table-context-extension';

const ACTIONS = ['addRowBefore', 'addRowAfter', 'addColumnBefore', 'addColumnAfter', 'deleteRow', 'deleteColumn', 'deleteTable', 'mergeCells', 'splitCell', 'toggleHeaderRow', 'toggleHeaderColumn'] as const;
export function DocumentTableContextMenu({ editor }: { editor: Editor }) {
    const { t } = useDocumentI18n();
    const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
    const pointRef = useRef<{ x: number; y: number } | null>(null);
    useEffect(() => {
        const open = (event: Event) => { pointRef.current = (event as CustomEvent).detail; setPoint(pointRef.current); };
        const close = ({ transaction }: { transaction: import('@tiptap/pm/state').Transaction }) => {
            // Radix's mount sequence blurs the editor and focuses a menu item;
            // TipTap surfaces both as blur/focus transactions with an UNCHANGED
            // selection and no doc change. Those are part of OPENING and must
            // not dismiss the menu (root cause of "requires repeated clicks").
            const meta = (transaction as unknown as { meta?: Record<string, unknown> }).meta ?? {};
            if ((meta.blur || meta.focus) && !transaction.docChanged && !transaction.selectionSet) return;
            // A real document change (remote edit, undo) or a permission drop
            // DOES invalidate the open menu.
            if (transaction.docChanged || !editor.isEditable) { pointRef.current = null; setPoint(null); }
        };
        editor.view.dom.addEventListener(TABLE_CONTEXT_EVENT, open);
        editor.on('transaction', close);
        return () => { editor.view.dom.removeEventListener(TABLE_CONTEXT_EVENT, open); editor.off('transaction', close); };
    }, [editor]);
    return <DropdownMenu open={!!point} onOpenChange={open => { if (!open) { pointRef.current = null; setPoint(null); } }} modal={false}>
        <DropdownMenuTrigger asChild><button type="button" aria-hidden tabIndex={-1} style={{ position: 'fixed', left: point?.x ?? -10000, top: point?.y ?? -10000, width: 0, height: 0, pointerEvents: 'none' }} /></DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={0} data-testid="document-table-context-menu" onCloseAutoFocus={e => e.preventDefault()}>
            {ACTIONS.map(action => <DropdownMenuItem key={action} data-testid={`document-table-${action}`}
                disabled={!editor.isEditable || !editor.can()[action]()}
                onSelect={() => { if (editor.isEditable && editor.can()[action]()) editor.chain().focus()[action]().run(); pointRef.current = null; setPoint(null); }}>
                {t(`document.toolbar.${action}`)}
            </DropdownMenuItem>)}
        </DropdownMenuContent>
    </DropdownMenu>;
}
